// Issues list page — professional table with sorting, pagination, quick actions
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table, TableHeader, TableBody, TableHead, TableRow,
} from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Plus, ChevronUp, ChevronDown, AlertTriangle, RefreshCw } from 'lucide-react'
import { LoadingSpinner } from '@/components/layout/LoadingSpinner'
import { IssuesStatsBar } from '@/components/issues/IssuesStatsBar'
import { IssuesFilterBar } from '@/components/issues/IssuesFilterBar'
import { IssuesTableRow } from '@/components/issues/IssuesTableRow'
import { IssuesTablePagination } from '@/components/issues/IssuesTablePagination'
import { IssuesEmptyState } from '@/components/issues/IssuesEmptyState'
import { useIssues } from '@/hooks/useIssues'
import type { IssueStatus } from '@/types/database'

type SortField = 'created_at' | 'title' | 'status' | 'priority' | 'd365_version'

const COLUMNS: { key: SortField | ''; label: string; sortable: boolean }[] = [
  { key: 'title', label: 'Title', sortable: true },
  { key: '', label: 'Customer', sortable: false },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'priority', label: 'Priority', sortable: true },
  { key: 'd365_version', label: 'Version', sortable: true },
  { key: '', label: 'Module', sortable: false },
  { key: 'created_at', label: 'Created', sortable: true },
  { key: '', label: '', sortable: false },
]

export function Issues(): JSX.Element {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<IssueStatus | '_all'>('_all')
  const [customerFilter, setCustomerFilter] = useState('')
  const [industryFilter, setIndustryFilter] = useState('')
  const [versionFilter, setVersionFilter] = useState('')
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(0)

  const { issues, totalCount, stats, filterOptions, isLoading, isStatsLoading, error, refetch } = useIssues({
    status: statusFilter === '_all' ? undefined : statusFilter,
    customerName: customerFilter || undefined,
    industry: industryFilter || undefined,
    d365Version: versionFilter || undefined,
    sortField, sortDirection: sortDir,
    page, pageSize: 20,
  })

  const hasFilters = statusFilter !== '_all' || !!customerFilter || !!industryFilter || !!versionFilter
  const clearFilters = () => {
    setStatusFilter('_all'); setCustomerFilter(''); setIndustryFilter(''); setVersionFilter('')
    setPage(0)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) { setSortDir((d) => d === 'asc' ? 'desc' : 'asc') }
    else { setSortField(field); setSortDir('asc') }
    setPage(0)
  }

  if (isLoading && issues.length === 0) {
    return <div className="flex h-[50vh] items-center justify-center"><LoadingSpinner message="Loading issues..." /></div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-kaycoo-teal-dark">Issues</h1>
          <p className="text-kaycoo-charcoal-2">Manage and track all reported D365 issues</p>
        </div>
        <Button onClick={() => navigate('/issues/new')} className="bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2">
          <Plus className="mr-1.5 h-4 w-4" /> New Issue
        </Button>
      </div>

      <IssuesStatsBar stats={stats} isLoading={isStatsLoading} />

      <IssuesFilterBar
        statusFilter={statusFilter} customerFilter={customerFilter}
        industryFilter={industryFilter} versionFilter={versionFilter}
        onStatusChange={(v) => { setStatusFilter(v); setPage(0) }}
        onCustomerChange={(v) => { setCustomerFilter(v); setPage(0) }}
        onIndustryChange={(v) => { setIndustryFilter(v); setPage(0) }}
        onVersionChange={(v) => { setVersionFilter(v); setPage(0) }}
        onClear={clearFilters}
        filterOptions={filterOptions}
      />

      {error && (
        <Alert variant="destructive" className="border-kaycoo-burgundy-dark bg-kaycoo-burgundy-pale">
          <AlertTriangle className="h-4 w-4 text-kaycoo-burgundy-dark" />
          <AlertDescription className="flex items-center justify-between text-kaycoo-burgundy-dark">
            <span>Failed to load issues: {error.message}</span>
            <Button variant="ghost" size="sm" onClick={() => refetch()} className="gap-1 text-kaycoo-burgundy-dark hover:bg-kaycoo-burgundy-pale">
              <RefreshCw className="h-3 w-3" /> Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card className="border-kaycoo-teal-light bg-kaycoo-white overflow-hidden">
        <CardContent className="p-0">
          {issues.length === 0 ? (
            <IssuesEmptyState hasFilters={hasFilters} onClearFilters={clearFilters} />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    {COLUMNS.map((col, i) => (
                      <TableHead
                        key={i}
                        className={col.sortable ? 'cursor-pointer select-none hover:text-kaycoo-teal-dark' : ''}
                        onClick={() => col.sortable && col.key && handleSort(col.key as SortField)}
                      >
                        <div className="flex items-center gap-1">
                          {col.label}
                          {col.sortable && sortField === col.key && (
                            sortDir === 'asc'
                              ? <ChevronUp className="h-3 w-3" />
                              : <ChevronDown className="h-3 w-3" />
                          )}
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {issues.map((issue) => <IssuesTableRow key={issue.id} issue={issue} />)}
                </TableBody>
              </Table>
              <IssuesTablePagination page={page} pageSize={20} total={totalCount} onPageChange={setPage} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
